// One status chip, rendered identically wherever a scan's progress needs
// showing — Story #5305 §5.2 (Task #5325): "a single status chip renders
// those stages plus elapsed time identically in every sub-view." Real,
// server-written state only (formatComputedAgo/formatScanStage) — never an
// invented "map ready"/percentage this component's props don't carry.
import { CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react'
import { formatComputedAgo, formatScanStage } from '../../lib/whitespace'

export type ScanChipState = 'not_scanned' | 'scanning' | 'updating' | 'ready' | 'failed'

interface ScanStatusChipProps {
  state: ScanChipState
  /** One of handler.py's SCAN_STAGE_* strings, when known. */
  stage?: string | null
  /** ISO timestamp of the last complete scan — drives "Computed N min ago". */
  completedAt?: string | null
}

const STYLES: Record<ScanChipState, { bg: string; border: string; text: string }> = {
  not_scanned: { bg: 'bg-gray-100', border: 'border-gray-200', text: 'text-gray-500' },
  scanning: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-iflow-700' },
  updating: { bg: 'bg-sky-50', border: 'border-sky-200', text: 'text-iflow-700' },
  ready: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700' },
  failed: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700' },
}

export default function ScanStatusChip({ state, stage, completedAt }: ScanStatusChipProps) {
  const { bg, border, text } = STYLES[state]
  const stageText = formatScanStage(stage)

  let label: string
  switch (state) {
    case 'scanning':
      label = stageText ? `Scanning… ${stageText}` : 'Scanning…'
      break
    case 'updating':
      label = stageText ? `Updating… ${stageText}` : 'Updating…'
      break
    case 'ready':
      label = `Scan ready · ${formatComputedAgo(completedAt ?? null)}`
      break
    case 'failed':
      label = 'Scan failed'
      break
    case 'not_scanned':
    default:
      label = 'Not yet scanned'
  }

  return (
    <span
      data-testid="scan-status-chip"
      data-state={state}
      className={`inline-flex items-center gap-1.5 ${bg} border ${border} ${text} text-[11.5px] font-bold rounded-full px-2.5 py-1`}
    >
      {(state === 'scanning' || state === 'updating') && <Loader2 className="w-3 h-3 animate-spin" />}
      {state === 'ready' && <CheckCircle2 className="w-3 h-3" />}
      {state === 'failed' && <XCircle className="w-3 h-3" />}
      {state === 'not_scanned' && <RefreshCw className="w-3 h-3" />}
      {label}
    </span>
  )
}
